export type PostLoginErrorKind = 'generic' | 'no-tenant'

/**
 * Error thrown by the CRITICAL section of the post-login load (identity + tenant).
 * Carries a `kind` so the UI can show an actionable message and the right actions
 * instead of an endless spinner.
 */
export class PostLoginLoadError extends Error {
  readonly kind: PostLoginErrorKind

  constructor(kind: PostLoginErrorKind, message?: string) {
    super(message ?? kind)
    this.name = 'PostLoginLoadError'
    this.kind = kind
  }
}

export interface NonCriticalLoad {
  name: string
  run: () => Promise<unknown>
}

/**
 * Run independent, NON-critical post-login loads (per-domain configs).
 *
 * Never rejects: a failing load — e.g. a 403 on a permission-gated endpoint for
 * an under-privileged user — is logged but must not block the other loads or
 * blank the whole app. Returns the names of the loads that failed (useful for a
 * follow-up toast).
 */
export const loadNonCritical = async (loads: readonly NonCriticalLoad[]): Promise<string[]> => {
  // async wrapper so a SYNCHRONOUS throw inside run() also becomes a rejected
  // result instead of escaping loadNonCritical and blanking the whole app.
  const results = await Promise.allSettled(
    loads.map(async load => {
      await load.run()
    }),
  )
  const failed: string[] = []

  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      const name = loads[index].name
      failed.push(name)
      // eslint-disable-next-line no-console
      console.error(`[post-login] non-critical load failed: ${name}`, result.reason)
    }
  })

  return failed
}
